import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { History, Copy, RotateCw, FileSpreadsheet, FileText } from "lucide-react";

const REPORT_LABELS = {
  sales: "Reporte de ventas",
  cash_flow: "Flujo diario de caja",
  kardex_movements: "Kardex por refacción",
  low_stock: "Bajo stock",
  slow_movement: "Movimientos lentos",
  accounts_payable: "Cuentas por pagar",
  supplier_payments: "Pagos a proveedores",
  purchase_returns: "Devoluciones a proveedor",
  technician_productivity: "Productividad por técnico",
};

const REPORT_PATHS = {
  sales: "/reportes/ventas",
  cash_flow: "/reportes/flujo",
  kardex_movements: "/inventario/kardex",
  low_stock: "/inventario/alertas",
  slow_movement: "/inventario/alertas",
  accounts_payable: "/cuentas-por-pagar",
  supplier_payments: "/pagos-proveedores",
  purchase_returns: "/devoluciones",
  technician_productivity: "/reportes/productividad",
};

export default function HistorialExportaciones() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const canGenerate = hasPermission("report_exports:generate");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKey, setFilterKey] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);

  const tenantParam = useMemo(
    () => (isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}),
    [isSuperAdmin, activeTenantId],
  );

  async function fetchList() {
    setLoading(true);
    try {
      const { data } = await api.get("/report-exports", {
        params: {
          ...tenantParam,
          report_key: filterKey !== "all" ? filterKey : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, dateFrom, dateTo, activeTenantId]);

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch { toast.error("No se pudo copiar"); }
  }

  function goToReport(item) {
    const path = REPORT_PATHS[item.report_key];
    if (!path) { toast.error("Ruta del reporte no disponible"); return; }
    // Pass filters via search params
    const q = new URLSearchParams();
    Object.entries(item.filters_snapshot || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") q.set(k, String(v));
    });
    window.location.href = `${path}${q.toString() ? "?" + q.toString() : ""}`;
  }

  return (
    <div className="space-y-6" data-testid="page-history-exports">
      <PageHeader
        title="Historial de exportaciones"
        description="Registro simple de reportes generados y compartidos. No se almacenan archivos — puedes regenerar con los mismos filtros."
      />

      <Card className="border-border">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={filterKey} onValueChange={setFilterKey}>
            <SelectTrigger data-testid="hist-filter-key"><SelectValue placeholder="Reporte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los reportes</SelectItem>
              {Object.entries(REPORT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="hist-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="hist-date-to" />
        </CardContent>
      </Card>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin historial" description="Aún no has descargado ni compartido reportes con los filtros actuales." icon={History} />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Reporte</TableHead>
                <TableHead>Formato</TableHead>
                <TableHead>Filas</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`hist-row-${it.id}`} className="tbl-row">
                  <TableCell className="text-xs">{it.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell className="text-sm">{REPORT_LABELS[it.report_key] || it.report_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {it.format === "pdf" ? <FileText className="h-3 w-3 mr-1 text-red-600" /> : <FileSpreadsheet className="h-3 w-3 mr-1 text-emerald-600" />}
                      {it.format?.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs mono">{it.row_count ?? "—"}</TableCell>
                  <TableCell className="text-xs">{it.branch_name || "—"}</TableCell>
                  <TableCell className="text-xs">{it.generated_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(it)} data-testid={`hist-view-${it.id}`}>
                      Ver
                    </Button>
                    {canGenerate && (
                      <Button variant="ghost" size="sm" onClick={() => goToReport(it)} data-testid={`hist-regen-${it.id}`}>
                        <RotateCw className="h-3.5 w-3.5 mr-1" /> Regenerar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="hist-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected ? (REPORT_LABELS[selected.report_key] || selected.report_name) : "Detalle"}</DialogTitle>
            <DialogDescription>
              {selected?.created_at?.slice(0, 16).replace("T", " ")} · {selected?.generated_by_name}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Filtros aplicados</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(selected.filters_snapshot || {}).filter(([_, v]) => v).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-xs">
                      <span className="text-muted-foreground mr-1">{k}:</span>
                      <span className="mono">{String(v)}</span>
                    </Badge>
                  ))}
                  {Object.keys(selected.filters_snapshot || {}).length === 0 && (
                    <span className="text-xs text-muted-foreground">Sin filtros</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Asunto sugerido</div>
                <div className="text-sm">{selected.subject_suggested || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Resumen</div>
                <Textarea rows={8} readOnly value={selected.share_summary_snapshot || ""} className="mono text-xs" data-testid="hist-summary" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => copyToClipboard(selected.share_summary_snapshot || "", "Resumen")} data-testid="hist-copy-summary">
                  <Copy className="h-4 w-4 mr-2" /> Copiar resumen
                </Button>
                <Button variant="outline" onClick={() => copyToClipboard(`Asunto: ${selected.subject_suggested}\n\n${selected.share_summary_snapshot}`, "Asunto + resumen")}>
                  <Copy className="h-4 w-4 mr-2" /> Copiar completo
                </Button>
                {canGenerate && (
                  <Button className="btn-primary-tenant" onClick={() => goToReport(selected)} data-testid="hist-goto-report">
                    <RotateCw className="h-4 w-4 mr-2" /> Volver a generar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
